import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, SUPER_TOKEN_KEY, TENANT_TOKEN_KEY } from '../../api/client';
import { channelLabel } from '../console/conversationChannelMeta';
import { DEMO_LEVEL_COLOR, DEMO_LEVEL_LABEL, formatDemoDate } from './demoUsageUi';
import type { DemoEngagementLevel, DemoOrgsUsageResponse, DemoOrgUsageSummary } from './superOrgTypes';

const LEVELS: DemoEngagementLevel[] = ['intenso', 'activo', 'poco', 'frio', 'sin_uso'];
const LEVEL_RANK: Record<DemoEngagementLevel, number> = {
  intenso: 0,
  activo: 1,
  poco: 2,
  frio: 3,
  sin_uso: 4,
};

type SortKey = 'name' | 'daysSinceCreated' | 'lastChat' | 'msgs7d' | 'channels' | 'level';

function channelSummary(row: DemoOrgUsageSummary): string {
  if (row.channels.length === 0) return '—';
  return row.channels
    .map((c) => `${channelLabel(c.channel)} (${c.conversations})`)
    .join(', ');
}

function lastChatMs(row: DemoOrgUsageSummary): number {
  const iso = row.lastVisitorMessageAt ?? row.lastConversationAt;
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function compareRows(a: DemoOrgUsageSummary, b: DemoOrgUsageSummary, key: SortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name, 'es');
    case 'daysSinceCreated':
      return a.daysSinceCreated - b.daysSinceCreated;
    case 'lastChat':
      return lastChatMs(a) - lastChatMs(b);
    case 'msgs7d':
      return a.messages.visitorLast7d - b.messages.visitorLast7d;
    case 'channels':
      return a.channels.length - b.channels.length;
    case 'level':
      return LEVEL_RANK[a.level] - LEVEL_RANK[b.level];
  }
}

export function SuperDemoUsagePage() {
  const nav = useNavigate();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(SUPER_TOKEN_KEY) : null;
  const [data, setData] = useState<DemoOrgsUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [levelFilter, setLevelFilter] = useState<DemoEngagementLevel | ''>('');
  const [sortKey, setSortKey] = useState<SortKey>('level');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [impersonateErr, setImpersonateErr] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr('');
    try {
      const d = await apiGet<DemoOrgsUsageResponse>('/api/super/demo-orgs/usage', token);
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      nav('/super/login', { replace: true });
      return;
    }
    void load();
  }, [token, nav, load]);

  const rows = useMemo(() => {
    const items = (data?.items ?? []).filter((r) => (levelFilter ? r.level === levelFilter : true));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => compareRows(a, b, sortKey) * dir);
  }, [data, levelFilter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'name' || key === 'level' ? 'asc' : 'desc');
  };

  const sortMark = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const impersonate = async (orgId: string) => {
    if (!token) return;
    setImpersonateErr('');
    try {
      const d = await apiPost<{ token: string }>(`/api/super/organizations/${orgId}/impersonate`, {}, token);
      localStorage.setItem(TENANT_TOKEN_KEY, d.token);
      window.open('/app', '_blank');
    } catch (e) {
      setImpersonateErr((e as Error).message);
    }
  };

  if (!token) return null;

  const totals = data?.totals;

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#f8fafc' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>Análisis de demos</h1>
        <p style={{ color: '#64748b', margin: '0 0 20px', fontSize: 14 }}>
          Empresas marcadas como demo: si están usando el chat, cuánto probaron y qué acción comercial conviene.
        </p>

        {err ? <p style={{ color: '#b91c1c' }}>{err}</p> : null}
        {impersonateErr ? <p style={{ color: '#b91c1c' }}>{impersonateErr}</p> : null}
        {loading ? <p style={{ color: '#64748b' }}>Cargando…</p> : null}

        {totals ?
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
            <SummaryChip label="Demos" value={totals.orgs} />
            {LEVELS.map((lv) => (
              <button
                key={lv}
                type="button"
                onClick={() => setLevelFilter((cur) => (cur === lv ? '' : lv))}
                style={{
                  border: `1px solid ${DEMO_LEVEL_COLOR[lv].border}`,
                  background: levelFilter === lv ? DEMO_LEVEL_COLOR[lv].bg : '#fff',
                  color: DEMO_LEVEL_COLOR[lv].fg,
                  borderRadius: 10,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  minWidth: 92,
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600 }}>{DEMO_LEVEL_LABEL[lv]}</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{totals.byLevel[lv]}</div>
              </button>
            ))}
          </div>
        : null}

        {!loading && data && data.items.length === 0 ?
          <p style={{ color: '#64748b' }}>
            No hay empresas marcadas como demo. Al crear una empresa, tildá «Empresa de demo», o activalo en el detalle.
          </p>
        : null}

        {rows.length > 0 ?
          <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', background: '#f8fafc', color: '#64748b' }}>
                  {(
                    [
                      ['name', 'Empresa'],
                      ['daysSinceCreated', 'Alta'],
                      ['lastChat', 'Último chat'],
                      ['msgs7d', 'Msgs 7d'],
                      ['channels', 'Canales'],
                      ['level', 'Nivel'],
                    ] as [SortKey, string][]
                  ).map(([key, label]) => (
                    <th key={key} style={{ padding: '10px 12px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>
                      <button
                        type="button"
                        onClick={() => toggleSort(key)}
                        style={{
                          border: 0,
                          background: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: 'inherit',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        {label}
                        {sortMark(key)}
                      </button>
                    </th>
                  ))}
                  <th style={{ padding: '10px 12px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Acción</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = DEMO_LEVEL_COLOR[r.level];
                  return (
                    <tr key={r.orgId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <Link
                          to={`/super/orgs/${r.orgId}/uso-demo`}
                          style={{ fontWeight: 700, color: '#312e81', textDecoration: 'none' }}
                        >
                          {r.name}
                        </Link>
                        <div style={{ color: '#94a3b8', fontSize: 12 }}>/{r.slug}</div>
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#475569' }}>
                        {r.daysSinceCreated} d
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#475569' }}>
                        {formatDemoDate(r.lastVisitorMessageAt ?? r.lastConversationAt)}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                        {r.messages.visitorLast7d}
                        <span style={{ color: '#94a3b8', fontWeight: 500 }}> vis.</span>
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: 220, color: '#475569' }}>{channelSummary(r)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: c.bg,
                            color: c.fg,
                            border: `1px solid ${c.border}`,
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {DEMO_LEVEL_LABEL[r.level]}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#334155', maxWidth: 280 }}>{r.action}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => void impersonate(r.orgId)}
                          style={{
                            border: '1px solid #ddd6fe',
                            background: '#fff',
                            color: '#6d28d9',
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Acceder
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        : null}
      </div>
    </div>
  );
}

function SummaryChip({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '10px 12px',
        minWidth: 92,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{value}</div>
    </div>
  );
}
